import Tooltip from "@mui/material/Tooltip";
import { filesize } from "filesize";
import { uniqueId } from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import { FiPlusCircle } from "react-icons/fi";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Cabecalho from "../../components/Cabecalho";
import Menu from "../../components/Menu";
import CmxButton from "../../components/ui/Button";
import CmxCheckBox from "../../components/ui/CheckBox";
import FileUploader from "../../components/ui/FileUploader";
import FileUploaderList from "../../components/ui/FileUploaderList";
import CmxInput, { CmxComboBox } from "../../components/ui/Input";
import { CampoEditorArquivos, temPalavras } from "../../components/EditorArquivos";
import { AuthContext } from "../../context/auth";
import { AcaoDe } from "../../enums/enums";
import { api } from "../../services/apiClient";
import { handleError } from "../../services/erros/ErrorHandler";

import "./style.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function NovaSolicitacao() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [assunto, setAssunto] = useState("");
  const [chamadoCliente, setChamadoCliente] = useState("");
  const [emailSolicitante, setEmailSolicitante] = useState(user.email);
  const [contatoTISelected, setContatoTISelected] = useState(user.id);
  const [tipoSelected, setTipoSelected] = useState("");
  const [prioridadeSelected, setPrioridadeSelected] = useState("");
  // Regra CHA-039 do Raptor: Descrição e Testes Realizados são editores com o print no meio do texto; o
  // conteúdo mora no editor e sai no Salvar (`coletar`).
  const descricaoRef = useRef(null);
  const testesRef = useRef(null);
  const [rotina, setRotina] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [servicoProdutos, setServicoProdutos] = useState([]);
  const [servicoProdutoSelected, setServicoProdutoSelected] = useState("");
  const [prioridades, setPrioridades] = useState([]);

  const [salvando, setSalvando] = useState(false);
  const [contatosTI, setContatosTI] = useState([]);
  const [checkedAreas, setCheckedKAreas] = useState([]);
  const [politicaIsCheked, setPoliticaIsCheked] = useState(false);

  // Usuário pode ter mais de um cliente vinculado (relação N:N). Mantém
  // compatibilidade com a forma antiga (cliente único direto no usuário)
  // como fallback, caso `user.clientes` não venha preenchido.
  const [clienteSelected, setClienteSelected] = useState(() =>
    Array.isArray(user?.clientes) && user.clientes.length === 1
      ? `${user.clientes[0].id}`
      : "",
  );

  const clientesList =
    Array.isArray(user?.clientes) && user.clientes.length > 0
      ? user.clientes
      : user && user.cliente_id
        ? [
            {
              id: user.cliente_id,
              label: user.cliente || "",
              chamado_cliente_obrigatorio: user.chamado_cliente_obrigatorio,
              servicos_produtos_ids: user.servicos_produtos_ids || "",
            },
          ]
        : [];

  const clienteSelecionado =
    clientesList.find((c) => `${c.id}` === `${clienteSelected}`) || null;
  const clienteDisabled = clientesList.length === 1;
  const chamadoClienteObrigatorio = clienteSelecionado
    ? clienteSelecionado.chamado_cliente_obrigatorio
    : user.chamado_cliente_obrigatorio;

  const checkboxes = [
    { area: "1", descricao: "Comercial" },
    { area: "2", descricao: "Financeiro" },
    { area: "3", descricao: "Industrial" },
    { area: "4", descricao: "Controladoria" },
  ];

  // Garante que o cliente único (se só houver um) fique selecionado. A tela é só de abertura: a
  // edição de chamado pelo Portal foi removida em 2026-10-08 (a rota nunca recebia o id e o endpoint
  // PUT /chamado/edit só servia a quem forjasse a requisição).
  useEffect(() => {
    setClienteSelected(clientesList.length === 1 ? `${clientesList[0].id}` : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Contatos de TI, Serviço/Produto e Prioridades dependem do cliente
  // selecionado (Prioridade não depende do cliente, mas é buscada junto).
  useEffect(() => {
    const fetchDependentes = async () => {
      try {
        if (!clienteSelected) {
          setContatosTI([]);
          setServicoProdutos([]);
          setContatoTISelected("");
          setServicoProdutoSelected("");
          return;
        }

        const [contatosResp, servicosResp, prioridadesResp] =
          await Promise.all([
            api.get("/usuario/contatosti", {
              params: { cliente_id: +clienteSelected },
            }),
            api.get("/servico-produto/cons", {
              params: { cliente_id: +clienteSelected },
            }),
            api.get("/prioridade/cons"),
          ]);

        const itensContatos = Array.isArray(contatosResp.data)
          ? contatosResp.data
          : [];
        const itensServicos = servicosResp.data || [];
        const itensPrioridades = Array.isArray(prioridadesResp.data)
          ? prioridadesResp.data
          : [];

        setContatosTI(itensContatos);
        setServicoProdutos(itensServicos);
        setPrioridades(itensPrioridades);

        const contatoPadrao = itensContatos.some(
          (c) => `${c.id}` === `${contatoTISelected}`,
        )
          ? `${contatoTISelected}`
          : itensContatos.some((c) => c.id === user.id)
            ? `${user.id}`
            : itensContatos.length === 1
              ? `${itensContatos[0].id}`
              : "";
        const servicoPadrao = itensServicos.some(
          (s) => `${s.id}` === `${servicoProdutoSelected}`,
        )
          ? `${servicoProdutoSelected}`
          : itensServicos.length === 1
            ? `${itensServicos[0].id}`
            : "";

        setContatoTISelected(contatoPadrao);
        setServicoProdutoSelected(servicoPadrao);
      } catch (error) {
        handleError(error, "Erro ao retornar dados");
      }
    };

    fetchDependentes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteSelected]);

  const handleCheckboxChange = (area, isChecked) => {
    if (isChecked) {
      setCheckedKAreas((prevAreas) => [...prevAreas, area]);
    } else {
      setCheckedKAreas((prevAreas) =>
        prevAreas.filter((prevArea) => prevArea !== area),
      );
    }
    setCheckedKAreas((prevKeys) =>
      [...prevKeys].sort((a, b) => a.localeCompare(b)),
    );
  };

  const handleUpload = (newFiles) => {
    const totalFiles = uploadedFiles.length + newFiles.length;

    if (totalFiles > 30) {
      Swal.fire({
        title: "Alerta!",
        text: "Máximo de anexos não pode ultrapassar 30.",
        icon: "warning",
      });
      return; // Impede a adição de novos arquivos se ultrapassar o limite
    }

    // Validar tamanho dos arquivos (40MB = 40 * 1024 * 1024 bytes)
    const maxFileSize = 40 * 1024 * 1024; // 40MB em bytes
    const oversizedFiles = newFiles.filter((file) => file.size > maxFileSize);

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((file) => file.name).join(", ");
      Swal.fire({
        title: "Arquivo muito grande!",
        text: `Os seguintes arquivos excedem o limite de 40MB: ${fileNames}. Por favor, selecione arquivos menores.`,
        icon: "error",
      });
      return; // Impede a adição de arquivos muito grandes
    }

    const files = newFiles.map((file) => ({
      file,
      id: uniqueId(),
      name: file.name,
      ReadableSize: filesize(file.size),
      preview: URL.createObjectURL(file),
      progress: 0,
      uploaded: false,
      error: false,
      url: null,
    }));

    setUploadedFiles([...uploadedFiles, ...files]);
  };

  const handleDelete = (fileId) => {
    // Lógica para excluir o arquivo com o ID correspondente
    const updatedFiles = uploadedFiles.filter((file) => file.id !== fileId);
    setUploadedFiles(updatedFiles);
  };

  async function handleSave(e) {
    e.preventDefault();

    if (!politicaIsCheked) {
      Swal.fire({
        title: "Alerta!",
        text: "Por favor, assinale a caixa referente à Política de Privacidade para continuar.",
        icon: "warning",
      });

      return;
    }

    if (!EMAIL_REGEX.test(emailSolicitante.trim())) {
      toast.warning(
        "Informe um e-mail válido no campo E-mail Solicitante (ex: nome@empresa.com).",
      );
      return;
    }

    setSalvando(true);

    try {
      // Os prints dos dois campos vão numa lista só: os da Descrição primeiro, os dos Testes em seguida
      // (o `[[arquivo:i]]` de cada um aponta para a posição dele nessa lista).
      const doTexto = descricaoRef.current.coletar(0);
      const dosTestes = testesRef.current.coletar(doTexto.arquivos.length);
      const printsNoTexto = [...doTexto.arquivos, ...dosTestes.arquivos];
      var descricaoNova = doTexto.texto;

      if (dosTestes.texto) {
        // Adicionar uma linha de separação
        descricaoNova += "\n-------------------------------------------\n";

        descricaoNova += "Testes Realizados\n";

        descricaoNova += dosTestes.texto;
      }

      const data = {
        cliente_id: +clienteSelected,
        assunto: assunto,
        solicitante_email: emailSolicitante,
        tipo: +tipoSelected,
        prioridade_id: +prioridadeSelected,
        rotina: rotina,
        descricao: descricaoNova,
        contato_ti_id: +contatoTISelected,
        chamado_cliente: chamadoCliente,
        area_atuacao: checkedAreas.join("^"),
        servico_produto_id: servicoProdutoSelected
          ? +servicoProdutoSelected
          : null,
      };

      if (
        data.cliente_id &&
        data.assunto &&
        (!chamadoClienteObrigatorio || data.chamado_cliente) &&
        data.solicitante_email &&
        data.contato_ti_id &&
        data.tipo &&
        data.prioridade_id &&
        temPalavras(doTexto.texto) &&
        data.servico_produto_id
      ) {
        const result = await api.post("/chamado/cad", data);

        // Regra CHA-039 do Raptor: cada arquivo entra na própria ação de abertura (`mensagem_id`), e não
        // numa ação solta — no chat e na Capa do Raptor, texto e arquivos aparecem juntos. O print colado no
        // texto leva a posição dele (`indice_no_texto`); o do campo Anexo(s) vai embaixo, como miniatura. Um
        // por requisição (o nginx do Portal limita cada envio a 50 MB) e um de cada vez, para os prints entrarem
        // na ordem e a barra de progresso andar arquivo a arquivo.
        let printsFalhos = 0;
        for (const [indice, file] of printsNoTexto.entries()) {
          if (!(await enviarPrintDoTexto(file, indice, result.data.id, result.data.mensagem_id))) printsFalhos += 1;
        }
        for (const file of uploadedFiles) {
          await processUpload(file, result.data.id, user.nome, result.data.mensagem_id);
        }
        if (printsFalhos) {
          toast.warning(
            printsFalhos === 1
              ? "O chamado foi aberto, mas 1 imagem do texto não foi enviada. Envie de novo pela conversa do chamado."
              : `O chamado foi aberto, mas ${printsFalhos} imagens do texto não foram enviadas. Envie de novo pela conversa do chamado.`,
          );
        }

        setAssunto("");
        setChamadoCliente("");
        setEmailSolicitante("");
        setContatoTISelected("");
        setTipoSelected("");
        setPrioridadeSelected("");
        descricaoRef.current.limpar();
        testesRef.current.limpar();
        setRotina("");
        setServicoProdutoSelected("");
        setCheckedKAreas([]);
        setUploadedFiles([]);

        Swal.fire({
          title: "Sucesso!",
          text: `O Chamado '${result.data.id}' foi cadastrado com sucesso!`,
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "Visualizar Chamado",
          cancelButtonText: "OK",
        }).then((response) => {
          if (response.isConfirmed) {
            navigate(`/app/chamados/${result.data.id}`);
          }
        });
      } else {
        toast.warning(`Por favor, preencha todos os campos obrigatórios.`);
      }
    } catch (error) {
      handleError(error, "Erro ao salvar Chamado");
    }

    setSalvando(false);
  }

  const updateFile = (id, data) => {
    setUploadedFiles((prevFiles) => {
      return prevFiles.map((file) =>
        file.id === id ? { ...file, ...data } : file,
      );
    });
  };

  // Print colado na Descrição ou nos Testes: entra na abertura no lugar do `[[arquivo:<indice>]]`.
  const enviarPrintDoTexto = async (file, indice, chamadoID, aberturaId) => {
    const data = new FormData();
    data.append("file", file);
    data.append("chamado_mensagem_id", aberturaId);
    data.append("indice_no_texto", indice);
    data.append("chamado_id", chamadoID);
    data.append("cliente", clienteSelecionado?.label || user.cliente);
    try {
      await api.post("chamadoacao/cad", data);
      return true;
    } catch {
      return false;
    }
  };

  const processUpload = async (uploadFile, chamadoID, criadoPor, aberturaId) => {
    const data = new FormData();

    data.append("file", uploadFile.file);
    if (aberturaId) data.append("chamado_mensagem_id", aberturaId);
    data.append("acao_de", AcaoDe.Cliente);
    data.append("criado_por", criadoPor);
    data.append("chamado_id", chamadoID);
    data.append("cliente", clienteSelecionado?.label || user.cliente);

    const result = await api
      .post("chamadoacao/cad", data, {
        onUploadProgress: (e) => {
          const progress = parseInt(Math.round((e.loaded * 100) / e.total));
          updateFile(uploadFile.id, {
            progress,
          });
        },
      })
      .then((response) => {
        updateFile(uploadFile.id, {
          uploaded: true,
          id: response.data.id,
          url: response.data.url,
        });
      })
      .catch((error) => {
        updateFile(uploadFile.id, {
          error: true,
        });
      });
  };

  return (
    <div>
      <Menu />
      <div className="content">
        <Cabecalho descricao="Novo Chamado">
          <FiPlusCircle size={24} />
        </Cabecalho>

        <div className="content_area">
          <form action="form-profile">
            <div className="row">
              <div className="col col-6">
                <div className="campo-com-icone">
                  <label>Cliente *</label>
                  <Tooltip
                    title={
                      <>
                        Selecione a conta/cliente para o qual o chamado será
                        registrado.
                      </>
                    }
                  >
                    <span>
                      <IoMdInformationCircleOutline size={15} />
                    </span>
                  </Tooltip>
                </div>
                <CmxComboBox
                  obrigatorio={true}
                  autoFocus={true}
                  value={clienteSelected}
                  disabled={clienteDisabled}
                  onChange={(e) => {
                    setClienteSelected(e.target.value);
                    setContatoTISelected("");
                    setServicoProdutoSelected("");
                  }}
                >
                  <option value="">
                    {clienteDisabled
                      ? "--Cliente definido--"
                      : "--Selecione um Cliente"}
                  </option>
                  {clientesList.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.label}
                    </option>
                  ))}
                </CmxComboBox>
              </div>
              <div className="col col-6">
                <div className="campo-com-icone">
                  <label>Serviço/Produto *</label>
                  <Tooltip
                    title={
                      <>Selecione um Serviço/Produto relacionado ao Chamado.</>
                    }
                  >
                    <span>
                      <IoMdInformationCircleOutline size={15} />
                    </span>
                  </Tooltip>
                </div>
                <CmxComboBox
                  obrigatorio={true}
                  value={servicoProdutoSelected}
                  disabled={!clienteSelected}
                  onChange={(e) => setServicoProdutoSelected(e.target.value)}
                >
                  <option value="">
                    {clienteSelected
                      ? "--Selecione um Serviço/Produto"
                      : "--Selecione primeiro um Cliente"}
                  </option>
                  {servicoProdutos.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.descricao}
                    </option>
                  ))}
                </CmxComboBox>
              </div>
            </div>

            <div className="row">
              <div className="col col-6">
                <div className="campo-com-icone">
                  <label>E-mail Solicitante *</label>
                  <Tooltip
                    title={
                      <>
                        Informe o e-mail do seu contato que solicitou o serviço.
                      </>
                    }
                  >
                    <span>
                      <IoMdInformationCircleOutline size={15} />
                    </span>
                  </Tooltip>
                </div>
                <CmxInput
                  type="email"
                  name="chamado-solicitante-email"
                  autoComplete="off"
                  maxLength={250}
                  obrigatorio={true}
                  value={emailSolicitante}
                  onChange={(e) => setEmailSolicitante(e.target.value)}
                />
              </div>
              <div className="col col-6">
                <label>Contato TI *</label>
                <CmxComboBox
                  obrigatorio={true}
                  value={contatoTISelected}
                  disabled={!clienteSelected}
                  onChange={(e) => setContatoTISelected(e.target.value)}
                >
                  <option value="">
                    {clienteSelected
                      ? "--Selecione um contato TI"
                      : "--Selecione primeiro um Cliente"}
                  </option>
                  {contatosTI.map((contato) => (
                    <option key={contato.id} value={contato.id}>
                      {`${contato.nome} (${contato.email})`}
                    </option>
                  ))}
                </CmxComboBox>
              </div>
            </div>

            <div className="row">
              <div className="col col-9">
                <label>Assunto *</label>
                <CmxInput
                  type="text"
                  maxLength={150}
                  obrigatorio={true}
                  value={assunto}
                  onChange={(e) => setAssunto(e.target.value)}
                />
              </div>
              <div className="col col-3">
                <div className="campo-com-icone">
                  <label>
                    {chamadoClienteObrigatorio
                      ? "Chamado Cliente *"
                      : "Chamado Cliente"}
                  </label>
                  <Tooltip
                    title={
                      <>Informe o número do chamado em seu sistema, se tiver.</>
                    }
                  >
                    <span>
                      <IoMdInformationCircleOutline size={15} />
                    </span>
                  </Tooltip>
                </div>
                <CmxInput
                  type="text"
                  maxLength={30}
                  obrigatorio={chamadoClienteObrigatorio}
                  value={chamadoCliente}
                  onChange={(e) => setChamadoCliente(e.target.value)}
                />
              </div>
            </div>

            {user.disponibilizar_area == 1 && (
              <div className="area">
                <label>Área *</label>

                <div className="checkbox-container">
                  {checkboxes.map((item) => (
                    <CmxCheckBox
                      key={item.area}
                      area={item.area}
                      descricao={item.descricao}
                      isChecked={checkedAreas.includes(item.area)}
                      onCheckboxChange={(isChecked) =>
                        handleCheckboxChange(item.area, isChecked)
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="row">
              <div className="col col-3">
                <div className="campo-com-icone">
                  <label>Tipo *</label>
                  <Tooltip
                    title={
                      <>
                        Serviço: uma solicitação nova.
                        <br />
                        Erro: algo não está como deveria.
                        <br />
                        Dúvida: necessita de suporte.
                      </>
                    }
                  >
                    <span>
                      <IoMdInformationCircleOutline size={15} />
                    </span>
                  </Tooltip>
                </div>
                <CmxComboBox
                  obrigatorio={true}
                  value={tipoSelected}
                  onChange={(e) => setTipoSelected(e.target.value)}
                >
                  <option value="">--Selecione um Tipo</option>
                  <option value="1">Dúvida</option>
                  <option value="2">Erro</option>
                  <option value="3">Serviço</option>
                </CmxComboBox>
              </div>

              <div className="col col-3">
                <div className="campo-com-icone">
                  <label>Prioridade *</label>
                  <Tooltip
                    title={
                      <>
                        Selecione a prioridade do chamado conforme as opções
                        cadastradas.
                      </>
                    }
                  >
                    <span>
                      <IoMdInformationCircleOutline size={15} />
                    </span>
                  </Tooltip>
                </div>
                <CmxComboBox
                  obrigatorio={true}
                  value={prioridadeSelected}
                  onChange={(e) => setPrioridadeSelected(e.target.value)}
                >
                  <option value="">--Selecione a Prioridade</option>
                  {prioridades.map((prioridade) => (
                    <option key={prioridade.id} value={prioridade.id}>
                      {prioridade.descricao ||
                        prioridade.nome ||
                        prioridade.label ||
                        `${prioridade.id}`}
                    </option>
                  ))}
                </CmxComboBox>
              </div>

              <div className="col col-3">
                <div className="campo-com-icone">
                  <label>Rotina(s)</label>
                  <Tooltip
                    title={
                      <>
                        Informe o nome da(s) rotina(s) ou tela(s) que serve de
                        ref. nesse chamado.
                        <br />
                        OBS: Separar com vírgula(,) caso tenha mais de uma(1).
                      </>
                    }
                  >
                    <span>
                      <IoMdInformationCircleOutline size={15} />
                    </span>
                  </Tooltip>
                </div>
                <CmxInput
                  type="text"
                  maxLength={250}
                  value={rotina}
                  onChange={(e) => setRotina(e.target.value)}
                />
              </div>
            </div>

            <div className="col">
              <label>Descrição *</label>
              <CampoEditorArquivos
                ref={descricaoRef}
                ariaLabel="Descrição"
                placeholder="Descreva a solicitação. Pode colar prints (Ctrl+V) no meio do texto."
                altura={150}
              />
            </div>

            <div className="col">
              <div className="campo-com-icone">
                <label>Testes Realizados</label>
                <Tooltip
                  title={
                    <>
                      Para o caso de chamados de erro, informe os
                      testes/verificações que você fez.
                      <br />
                      Isso ajuda no diagnóstico do problema.
                    </>
                  }
                >
                  <span>
                    <IoMdInformationCircleOutline size={15} />
                  </span>
                </Tooltip>
              </div>
              <CampoEditorArquivos ref={testesRef} ariaLabel="Testes Realizados" altura={80} />
            </div>

            <div className="col">
              <label>Anexo(s)</label>
              <FileUploader onFilesChange={handleUpload} />
              {!!uploadedFiles.length && (
                <FileUploaderList
                  files={uploadedFiles}
                  onDelete={handleDelete}
                />
              )}
            </div>

            <div className="col politica_privacidade">
              <CmxCheckBox
                fontSize={11}
                width={"100%"}
                height={"100%"}
                descricao={`Ao informar dados à Customax, sua empresa será responsável pelo compartilhamento como Controladora de Dados.
                Siga a Lei Geral de Proteção de Dados (Lei 13.709/2018) e as diretrizes legais. Compartilhe apenas dados necessários.
                Não compartilhe dados sensíveis. Consulte nossa 'Política de Privacidade'. Para dúvidas, contate nosso canal LGPD.`}
                isChecked={politicaIsCheked}
                onCheckboxChange={(isChecked) => setPoliticaIsCheked(isChecked)}
              />
            </div>

            <CmxButton
              loading={salvando}
              enabled={politicaIsCheked}
              style={{ marginLeft: "12px", marginTop: "12px" }}
              type="submit"
              onClick={handleSave}
            >
              Salvar
            </CmxButton>
          </form>
        </div>
      </div>
    </div>
  );
}
